import { useCallback, useEffect, useState } from "react";
import {
  EncryptionWarning,
  SettingField,
  TestButton,
  type SettingFieldData,
} from "../components/SettingField";
import { useI18n, serverError } from "../i18n";

interface SettingsPayload {
  encryptionReady: boolean;
  fields: SettingFieldData[];
}

/**
 * 郵件設定（Resend）
 * ---------------------------------------------------------------
 *
 * 全站的交易信 —— 註冊驗證、忘記密碼、提案通知、撥款通知 ——
 * 都靠這把金鑰。它失效的時候整個平台不會壞掉，
 * 但使用者會卡在「收不到驗證信」，而且沒有任何錯誤畫面告訴他們原因。
 *
 * 所以這一頁的重點是**讓失效變得看得見**：
 * 狀態標籤、測試連線、以及旁邊的寄信紀錄。
 */
export default function SettingsEmail() {
  const { t } = useI18n();
  const [data, setData] = useState<SettingsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch("/api/settings/email", { credentials: "same-origin" });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(serverError(payload, t, `HTTP ${res.status}`));
      setData(payload.data as SettingsPayload);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <p className="rounded-card nm-inset p-6 text-sm text-brand-300">{error}</p>;
  if (!data) return <div className="h-80 rounded-card shimmer" />;

  const field = (key: string) => data.fields.find((f) => f.key === key);

  return (
    <div className="max-w-2xl space-y-5">
      {!data.encryptionReady && <EncryptionWarning service="notifications" />}

      <section className="rounded-card nm p-5">
        <h2 className="text-sm font-semibold text-ink">Resend</h2>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">
          {t("se.lede")}{" "}
          <a
            href="https://resend.com/api-keys"
            target="_blank"
            rel="noreferrer noopener"
            className="text-brand-300 underline underline-offset-2"
          >
            resend.com/api-keys
          </a>{" "}
          {t("se.ledeTail")}
        </p>

        <div className="mt-4 space-y-3">
          {field("resend_api_key") && (
            <SettingField
              field={{ ...field("resend_api_key")!, secret: true }}
              endpoint="/settings/email"
              placeholder="re_..."
              hint={t("se.keyHint")}
              onSaved={load}
            />
          )}

          {field("email_from") && (
            <SettingField
              field={field("email_from")!}
              endpoint="/settings/email"
              placeholder="ColoureDev <no-reply@colouredev.com>"
              hint={t("se.fromHint")}
              onSaved={load}
            />
          )}
        </div>

        <TestButton endpoint="/settings/email/test" label={t("se.test")} />
        <p className="mt-2 text-[11px] text-ink-muted">
          {t("se.testNote")}
        </p>
      </section>
    </div>
  );
}
